/* A sub's documents as a checklist (Duncan 2026-10-22: the Documents tab "isn't very user-friendly and it took me a
   while to figure out even how to edit the checks"): one row per paper it owes -- a job's certificate of insurance
   and job site safety plan (a project's Subcontractors tab, and each job on the sub's page), and the company's MSA,
   W-9 and (optional) prequalification (the sub's page in Subcontractor Mgmt only: "required for the subcontractor to
   do work", collected "before the work is even assigned") -- each saying where it stands, with its Upload right
   there when it's missing. A certificate's six checks are boxes: a click saves at once, with an Undo (Duncan:
   "should save it straight away"). A flag elsewhere opens its row, highlighted. */
import React, { useEffect, useRef, useState } from 'react';
import { Icon, IconBtn, Menu } from '../ui';
import { SecondaryButton } from './SubUi';
import { CopyWording } from './JobDocuments';
import { COI_CHECKS, coiState, expiryStatus, fmtDay, fmtSize, moneyOfCents, todayYmd } from '../../lib/subcontractors';

const muted = { fontSize: 12.5, color: 'var(--text-3)' };
const linkBtn = { border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', fontFamily: 'inherit', whiteSpace: 'nowrap' };
const smallBtn = { height: 28, padding: '0 10px', fontSize: 13 };
const fmtAdded = (ts) => (ts ? new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
/** A check's words for this job: "the owner" becomes its name. */
const checkLabel = (c, owner) => c.label.replace('the owner', owner || 'the owner');

const TONE = {
  ok: { color: 'var(--success)', soft: 'var(--success-soft)', icon: 'check' },
  warn: { color: 'var(--warning)', soft: 'var(--warning-soft)', icon: 'alert-triangle' },
  bad: { color: 'var(--danger)', soft: 'var(--danger-soft)', icon: 'alert' },
  none: { color: 'var(--text-3)', soft: 'transparent', icon: null },
  file: { color: 'var(--text-3)', soft: 'var(--bg-surface-2)', icon: 'template' }, // a paper with nothing to chase
};

/** Highlights its element for a moment, scrolled into view, each time `focus` names it (a flag was clicked). */
function useFocusFlash(active, seq) {
  const ref = useRef(null);
  const [lit, setLit] = useState(false);
  useEffect(() => {
    if (!active) { setLit(false); return undefined; } // another flag took over: this one goes out at once
    if (ref.current && ref.current.scrollIntoView) ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setLit(true);
    const t = setTimeout(() => setLit(false), 2600);
    return () => clearTimeout(t);
  }, [active, seq]);
  return [ref, lit];
}

/* ---- A job's papers as cards (Duncan 2026-10-23: the rows didn't "match the modern and easy appeal of other parts of
   our system"; the mockup he OK'd): one card per paper, side by side on a wide screen -- where it stands as a pill, its
   file as a tile (View, Download, the rest under ⋯), a certificate's six checks as a list with "N of 6" (a click still
   saves at once, with an Undo), and a place to drop or choose the file when it's missing. */
const JD_CSS = `
  .jd-check:hover { background: color-mix(in oklch, var(--bg-hover) 60%, transparent) !important; }
  .jd-check:focus-visible, .jd-drop:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--accent-ring); }
  .jd-drop:hover { border-color: var(--accent) !important; background: color-mix(in oklch, var(--accent-soft) 60%, transparent) !important; }
`;
const pillOf = (tone) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
  color: TONE[tone].color, background: tone === 'none' ? 'var(--bg-surface-2)' : TONE[tone].soft,
});
/** The cards' grid: side by side while each gets `min` px, stacked below that. */
const gridOf = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`, gap: 12, padding: 12, background: 'var(--bg-surface-2)', alignItems: 'start' });
const extOf = (name) => { const m = /\.([a-z0-9]{2,4})$/i.exec(name || ''); return m ? m[1].toUpperCase() : 'FILE'; };

/** One paper's card: its mark, name and a line under it, its state as a pill; what's on file below. A flag elsewhere
    lights it up, scrolled into view. */
function DocCard({ rowKey, focus, tone, title, sub, pill, children }) {
  const [ref, lit] = useFocusFlash(!!focus && focus.key === rowKey, focus && focus.seq);
  return (
    <div ref={ref} data-doc-row={rowKey} style={{
      display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px', borderRadius: 12, minWidth: 0,
      border: '1px solid var(--border-subtle)', background: lit ? 'var(--accent-soft)' : 'var(--bg-surface)',
      boxShadow: lit ? '0 0 0 2px var(--accent)' : 'var(--shadow-xs)', transition: 'box-shadow 0.3s, background-color 0.3s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span aria-hidden="true" style={{
          width: 32, height: 32, flex: '0 0 32px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: TONE[tone].color, background: tone === 'none' ? 'transparent' : TONE[tone].soft, border: tone === 'none' ? '1.5px dashed var(--border-strong)' : 'none',
        }}>
          {TONE[tone].icon && <Icon name={TONE[tone].icon} size={16} stroke={2.2} />}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 650, color: 'var(--text-1)' }}>{title}</div>
          {sub && <div style={muted}>{sub}</div>}
        </div>
        {pill && <span style={pillOf(tone)}>{tone === 'ok' && <Icon name="check" size={12} stroke={2.4} />}{pill}</span>}
      </div>
      {children}
    </div>
  );
}

/** A file on a card: its type, name and what's known of it, then View and Download, and the rest under ⋯. */
function FileTile({ doc, meta, onView, onDownload, more, superseded }) { // onView / onDownload: null when this person can't open it
  const ext = extOf(doc.filename);
  const items = more.filter(Boolean);
  return (
    // On a narrow card the buttons drop below the name rather than squeeze it into a sliver.
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 10px', padding: '8px 8px 8px 10px', borderRadius: 10, background: 'var(--bg-surface-2)', minWidth: 0, opacity: superseded ? 0.72 : 1 }}>
      <span aria-hidden="true" className="mono" style={{
        flex: '0 0 auto', minWidth: 34, height: 24, padding: '0 5px', borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10.5, fontWeight: 700, color: '#fff', background: ext === 'PDF' ? 'var(--danger)' : 'var(--accent)',
      }}>{ext}</span>
      <div style={{ flex: '1 1 170px', minWidth: 0 }}>
        <div title={doc.filename} style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {doc.filename}{superseded && <span style={{ ...muted, fontWeight: 600 }}> · superseded</span>}
        </div>
        <div style={{ ...muted, fontSize: 12 }}>{meta}</div>
      </div>
      <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 'auto' }}>
        {onView && <IconBtn name="eye" label={`View ${doc.filename}`} onClick={() => onView(doc)} />}
        {onDownload && <IconBtn name="download" label={`Download ${doc.filename}`} onClick={() => onDownload(doc)} />}
        {items.length > 0 && <Menu trigger={(toggle) => <IconBtn name="kebab" label={`More for ${doc.filename}`} onClick={toggle} />} items={items} />}
      </span>
    </div>
  );
}

/** A certificate's six checks as a list: a tick or a ring (red when it doesn't name the job), its "N of 6" above.
    Unticked, a check says why. A click saves at once; "Saved · Undo" for a few seconds after. */
function CoiCheckList({ doc, owner, onToggleCheck }) {
  const [busy, setBusy] = useState(null); // the check being saved
  const [saved, setSaved] = useState(null); // { key, was }: the last change, for its Undo
  const [error, setError] = useState(null);
  useEffect(() => { if (!saved) return undefined; const t = setTimeout(() => setSaved(null), 6000); return () => clearTimeout(t); }, [saved]);
  const flip = async (key, to, undo) => {
    if (busy) return;
    setBusy(key);
    setError(null);
    try {
      await onToggleCheck(doc, key, to);
      setSaved(undo ? null : { key, was: !to });
    } catch (e) {
      setError(e.message || 'Couldn’t save that. Try again.');
    } finally {
      setBusy(null);
    }
  };
  const notes = (doc.scan && doc.scan.notes) || {};
  const found = (doc.scan && doc.scan.found) || {};
  const done = COI_CHECKS.filter((c) => !!doc[c.key]).length;
  const bar = !doc.names_job ? 'var(--danger)' : done === COI_CHECKS.length ? 'var(--success)' : 'var(--warning)';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>What it shows</span>
        <span className="mono" style={{ ...muted, fontSize: 12 }}>{done} of {COI_CHECKS.length}</span>
        <span aria-hidden="true" style={{ flex: 1, height: 4, borderRadius: 2, background: `color-mix(in oklch, ${bar} 18%, transparent)` }}>
          <span style={{ display: 'block', width: `${(done / COI_CHECKS.length) * 100}%`, height: '100%', borderRadius: 2, background: bar }} />
        </span>
      </div>
      <div role="group" aria-label="What the certificate shows">
        {COI_CHECKS.map((c, i) => {
          const on = !!doc[c.key];
          const tone = on ? 'var(--success)' : c.key === 'names_job' ? 'var(--danger)' : 'var(--warning)';
          const why = c.key === 'names_job' ? 'not on it: the certificate doesn’t count for this job' : notes[c.key] ? notes[c.key].text : 'not on it';
          const quote = found[c.key] || (notes[c.key] && notes[c.key].quote);
          return (
            <button key={c.key} type="button" role="checkbox" aria-checked={on} aria-busy={busy === c.key || undefined} disabled={busy === c.key}
              className="jd-check" onClick={() => flip(c.key, !on)} title={quote ? `On the certificate: ${quote}` : 'Click to change it'}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 9, width: '100%', textAlign: 'left', padding: '7px 6px', margin: 0, border: 'none',
                borderTop: i ? '1px solid var(--border-subtle)' : 'none', borderRadius: 0, background: 'transparent', fontFamily: 'inherit',
                cursor: busy === c.key ? 'progress' : 'pointer', opacity: busy === c.key ? 0.6 : 1,
              }}>
              <span aria-hidden="true" style={{
                flex: '0 0 18px', width: 18, height: 18, marginTop: 1, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: on ? 'var(--success)' : 'transparent', border: on ? 'none' : `1.5px solid ${tone}`, color: '#fff',
              }}>
                {on && <Icon name="check" size={12} stroke={2.8} />}
              </span>
              <span style={{ minWidth: 0, fontSize: 13.5, lineHeight: 1.35, color: 'var(--text-1)' }}>
                {checkLabel(c, owner)}
                {!on && <span style={{ display: 'block', fontSize: 12, color: tone }}>{why}</span>}
              </span>
            </button>
          );
        })}
      </div>
      <div role="status" aria-live="polite" style={{ ...muted, fontSize: 12, minHeight: 18, marginTop: 4 }}>
        {error ? <span style={{ color: 'var(--danger)' }}>{error}</span> : saved ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon name="check" size={12} stroke={2.4} style={{ color: 'var(--success)' }} />Saved
            <span aria-hidden="true">·</span>
            <button type="button" onClick={() => flip(saved.key, saved.was, true)} style={linkBtn}>Undo</button>
          </span>
        ) : 'Click one to change it'}
      </div>
    </div>
  );
}

/** Where a missing paper goes: drop the file on it, or click to choose one; either opens the upload with it. */
function DropZone({ label, onFile }) {
  const [over, setOver] = useState(false);
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1}
        onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) onFile(f); }} />
      <button type="button" className="jd-drop" onClick={() => ref.current && ref.current.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]; if (f) onFile(f); }}
        style={{
          width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '22px 16px', borderRadius: 12, cursor: 'pointer',
          border: `1.5px dashed ${over ? 'var(--accent)' : 'var(--border-strong)'}`, background: over ? 'var(--accent-soft)' : 'transparent', fontFamily: 'inherit',
        }}>
        <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-surface-2)', color: 'var(--text-2)' }}>
          <Icon name="upload" size={18} />
        </span>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>{label}</span>
        <span style={muted}>Drop the file here, or click to choose one</span>
      </button>
    </>
  );
}

/** Where a job's certificates stand, said for its card: { tone, pill, sub }. */
function coiRowState(docs, needs, today) {
  const coi = coiState(docs, today);
  const follow = coi.followUps.length;
  const followText = `${follow} to follow up`;
  const until = coi.doc && coi.doc.expiry_date ? `Good to ${fmtDay(coi.doc.expiry_date)}` : 'On file';
  if (!needs) return coi.level === 'none' ? { tone: 'none', pill: 'None on file', sub: null } : { tone: 'ok', pill: 'On file', sub: coi.text };
  switch (coi.level) {
    case 'none': return { tone: 'bad', pill: 'Missing', sub: 'Send the agent the COI wording, then upload what comes back' };
    case 'unnamed': return { tone: 'bad', pill: 'Doesn’t name this job', sub: until };
    case 'expired': return { tone: 'bad', pill: 'Expired', sub: `Expired ${fmtDay(coi.doc.expiry_date)}` };
    case 'expiring': return { tone: 'warn', pill: follow ? followText : 'Expiring soon', sub: `Expires ${fmtDay(coi.doc.expiry_date)}` };
    default: return { tone: follow ? 'warn' : 'ok', pill: follow ? followText : 'In place', sub: until };
  }
}

/**
 * A job's papers for one sub, as two cards: its certificate(s) of insurance -- each with its six checks to click --
 * and its job site safety plan.
 *   job      { name, number, owner, needs_papers }: a finished job flags nothing
 *   focus    { key: 'coi' | 'safety', seq }: the card a flag asked for
 *   onAdd(kind, file?) · onReplace(doc) · onToggleCheck(doc, key, value) · onEdit(doc) · onRemove(doc) · onView(doc) · onDownload(doc)
 */
export function JobDocRows({ job, docs, contract = null, today = todayYmd(), wording, canAdd = true, focus, onAdd, onReplace, onToggleCheck, onEdit, onRemove, onView, onDownload }) {
  const needs = !job || job.needs_papers !== false;
  const cois = docs.filter((d) => d.doc_type === 'insurance')
    .sort((a, b) => String(b.expiry_date || '').localeCompare(String(a.expiry_date || '')) || b.id - a.id);
  const plans = docs.filter((d) => d.doc_type === 'safety');
  const coi = coiRowState(docs, needs, today);
  const more = (d, dates) => (canAdd ? [
    dates && { label: 'Change dates', onClick: () => onEdit(d) },
    { label: 'Replace', onClick: () => onReplace(d) },
    { divider: true },
    { label: 'Remove', danger: true, onClick: () => onRemove(d) },
  ] : []);
  const added = (d) => `${fmtSize(d.size_bytes)} · ${d.uploaded_by_name ? `added by ${d.uploaded_by_name}, ` : ''}${fmtAdded(d.created_at)}`;
  // The wording is one for every job, kept in Subcontractor Mgmt (Admin, VP, Accounting: Duncan 2026-10-23); a job only
  // copies it, filled in with its own name, number and owner.
  const wordingBtn = wording ? <CopyWording wording={wording} job={job} /> : null;
  const safety = plans.length ? { tone: 'ok', pill: 'In place' } : needs ? { tone: 'bad', pill: 'Missing' } : { tone: 'none', pill: 'None on file' };
  // The subcontract (Duncan 2026-10-29): the executed one, with the amount typed as it was added -- the sub's billing
  // builds on that amount. Its menu's Change amount is the edit window.
  const subcontracts = docs.filter((d) => d.doc_type === 'subcontract');
  const subcontract = subcontracts.length ? { tone: 'ok', pill: 'In place' } : needs ? { tone: 'bad', pill: 'Missing' } : { tone: 'none', pill: 'None on file' };
  const amount = contract ? moneyOfCents(contract.amount_cents) : null;
  const subMore = (d) => (canAdd ? [
    { label: 'Change amount', onClick: () => onEdit(d) },
    { label: 'Replace', onClick: () => onReplace(d) },
    { divider: true },
    { label: 'Remove', danger: true, onClick: () => onRemove(d) },
  ] : []);
  return (
    <div style={gridOf(340)}>
      <style>{JD_CSS}</style>
      <DocCard rowKey="coi" focus={focus} tone={coi.tone} title="Certificate of insurance" sub={coi.sub} pill={coi.pill}>
        {cois.map((d, i) => {
          const expiry = d.expiry_date ? expiryStatus(d.expiry_date, today) : null;
          return (
            <div key={d.id} style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: i ? 12 : 0, borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
              <FileTile doc={d} onView={onView} onDownload={onDownload} more={more(d, true)}
                meta={(
                  <>
                    {d.effective_date ? fmtDay(d.effective_date) : '…'} → {d.expiry_date ? fmtDay(d.expiry_date) : '…'}
                    {expiry && expiry.tone && <span style={{ color: expiry.tone, fontWeight: 600 }}> · {expiry.text}</span>}
                    {d.number && <span className="mono"> · #{d.number}</span>}
                    {' · '}{added(d)}{d.scan && d.scan.status === 'read' && ' · read by Summit'}
                  </>
                )} />
              <CoiCheckList doc={d} owner={job && job.owner} onToggleCheck={onToggleCheck} />
            </div>
          );
        })}
        {!cois.length && canAdd && <DropZone label="Upload the certificate" onFile={(f) => onAdd('insurance', f)} />}
        {canAdd && (wordingBtn || cois.length > 0) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {wordingBtn}
            {cois.length > 0 && <SecondaryButton onClick={() => onAdd('insurance')} style={smallBtn}><Icon name="plus" size={13} />Add another certificate</SecondaryButton>}
          </div>
        )}
      </DocCard>
      <DocCard rowKey="safety" focus={focus} tone={safety.tone} title="Job site safety plan" sub="One for each job, specific to it" pill={safety.pill}>
        {plans.map((d) => <FileTile key={d.id} doc={d} onView={onView} onDownload={onDownload} more={more(d, false)} meta={added(d)} />)}
        {!plans.length && canAdd && <DropZone label="Upload the safety plan" onFile={(f) => onAdd('safety', f)} />}
      </DocCard>
      <DocCard rowKey="subcontract" focus={focus} tone={subcontract.tone} title="Subcontract" sub="The executed subcontract for this job, with its amount" pill={subcontract.pill}>
        {subcontracts.map((d) => (
          <FileTile key={d.id} doc={d} onView={onView} onDownload={onDownload} more={subMore(d)}
            meta={(
              <>
                {amount && <b style={{ fontWeight: 650, color: 'var(--text-1)' }}>{amount}</b>}
                {d.signed_date && <> · signed {fmtDay(d.signed_date)}</>}
                {d.number && <span className="mono"> · CE #{d.number}</span>}
                {' · '}{added(d)}
              </>
            )} />
        ))}
        {!subcontracts.length && amount && <div style={muted}>Amount {amount}; its PDF was removed. Upload it again:</div>}
        {!subcontracts.length && canAdd && <DropZone label="Upload the subcontract" onFile={(f) => onAdd('subcontract', f)} />}
      </DocCard>
    </div>
  );
}

// The company papers, in the order they're owed: MSA and W-9 once per sub; prequalification when asked for.
const COMPANY = [
  { kind: 'msa', title: 'MSA', what: 'Master service agreement', drop: 'Upload the MSA' },
  { kind: 'w9', title: 'W-9', what: null, drop: 'Upload the W-9' },
  { kind: 'prequal', title: 'Prequalification', what: 'Optional', drop: 'Upload a prequalification', optional: true },
];
const addedOf = (d) => `${fmtSize(d.size_bytes)} · ${d.uploaded_by_name ? `added by ${d.uploaded_by_name}, ` : ''}${fmtAdded(d.created_at)}`;

/**
 * The sub's company papers -- MSA, W-9, prequalification -- once for every job, as cards like a job's (2026-10-23).
 *   company   { msa, w9, prequal }: the current copy of each, or null
 *   history   { msa: [...], ... }: every copy, newest first (older ones show as superseded)
 *   canAdd    upload one; canOpen: view, download, change, remove one (Subcontractor Mgmt: a W-9 carries the tax ID)
 *   kinds     which papers to show (a job's Subcontractors tab: the MSA and W-9, read only); all of them when left out
 *   onAdd(kind, file?)
 */
export function CompanyDocRows({ company, history, today = todayYmd(), canAdd = true, canOpen = false, kinds, focus, onAdd, onEdit, onRemove, onView, onDownload, footer }) {
  return (
    <div style={gridOf(280)}>
      <style>{JD_CSS}</style>
      {COMPANY.filter((p) => !kinds || kinds.includes(p.kind)).map((p) => {
        const doc = company && company[p.kind];
        const copies = history ? history[p.kind] || [] : doc ? [doc] : [];
        const expiry = doc && doc.expiry_date ? expiryStatus(doc.expiry_date, today) : null;
        const lapsed = expiry && expiry.level === 'expired';
        const expiring = expiry && expiry.level === 'expiring';
        const tone = !doc ? (p.optional ? 'none' : 'bad') : lapsed ? 'bad' : expiring ? 'warn' : 'ok';
        const pill = !doc ? (p.optional ? 'None on file' : 'Missing') : lapsed ? 'Expired' : expiring ? 'Expiring soon' : 'In place';
        const since = !doc ? null : doc.signed_date ? `Signed ${fmtDay(doc.signed_date)}` : doc.effective_date ? `Effective ${fmtDay(doc.effective_date)}` : 'On file';
        return (
          <DocCard key={p.kind} rowKey={p.kind} focus={focus} tone={tone} title={p.title} pill={pill}
            sub={doc ? `${since}${expiry && expiry.tone ? ` · ${expiry.text}` : ''}` : p.what}>
            {copies.map((d) => (
              <FileTile key={d.id} doc={d} superseded={d.current === false}
                onView={canOpen ? onView : null} onDownload={canOpen ? onDownload : null}
                more={[
                  canOpen && { label: 'Change details', onClick: () => onEdit(d) },
                  canAdd && d.current !== false && { label: 'Upload newer copy', onClick: () => onAdd(p.kind) },
                  canOpen && { divider: true },
                  canOpen && { label: 'Remove', danger: true, onClick: () => onRemove(d) },
                ]}
                meta={`${[d.signed_date && `signed ${fmtDay(d.signed_date)}`, d.expiry_date && `expires ${fmtDay(d.expiry_date)}`].filter(Boolean).join(' · ')}${d.signed_date || d.expiry_date ? ' · ' : ''}${addedOf(d)}`} />
            ))}
            {!doc && canAdd && <DropZone label={p.drop} onFile={(f) => onAdd(p.kind, f)} />}
          </DocCard>
        );
      })}
      {footer}
    </div>
  );
}

/** A section of cards: a quiet heading (its action beside it), the cards under it. */
export const DocSection = ({ title, note, action, children }) => (
  <section aria-label={title} style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, background: 'var(--bg-surface)', overflow: 'hidden', marginBottom: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px 10px', flexWrap: 'wrap', padding: '9px 14px 8px', background: 'var(--bg-surface-2)' }}>
      <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{title}</span>
      {action}
      {note && <span style={muted}>{note}</span>}
    </div>
    {children}
  </section>
);

/** Anything else on file -- licenses, general certificates, other papers -- a card per kind (2026-10-23): each file
    with what's known of it, an expired or expiring one said on the card. */
export function OtherDocList({ groups, today = todayYmd(), onView, onDownload, onEdit, onRemove }) {
  return (
    <div style={gridOf(300)}>
      <style>{JD_CSS}</style>
      {groups.map(({ type, docs }) => {
        const levels = docs.filter((d) => d.current !== false && d.expiry_date).map((d) => expiryStatus(d.expiry_date, today).level);
        const tone = levels.includes('expired') ? 'bad' : levels.includes('expiring') ? 'warn' : 'file';
        const pill = levels.includes('expired') ? 'Expired' : levels.includes('expiring') ? 'Expiring soon' : null;
        return (
          <DocCard key={type.value} rowKey={`other-${type.value}`} tone={tone} title={type.group} pill={pill} sub={`${docs.length} file${docs.length === 1 ? '' : 's'}`}>
            {docs.map((d) => {
              const expiry = d.expiry_date && d.current !== false ? expiryStatus(d.expiry_date, today) : null;
              const dates = [d.signed_date && `signed ${fmtDay(d.signed_date)}`, d.effective_date && `effective ${fmtDay(d.effective_date)}`, d.expiry_date && `expires ${fmtDay(d.expiry_date)}`].filter(Boolean);
              return (
                <FileTile key={d.id} doc={d} superseded={d.current === false} onView={onView} onDownload={onDownload}
                  more={[{ label: 'Change details', onClick: () => onEdit(d) }, { divider: true }, { label: 'Remove', danger: true, onClick: () => onRemove(d) }]}
                  meta={(
                    <>
                      {d.title && d.title !== type.title ? `${d.title} · ` : ''}{d.number && <span className="mono">#{d.number} · </span>}
                      {dates.length ? `${dates.join(' · ')} · ` : ''}
                      {expiry && expiry.tone && <span style={{ color: expiry.tone, fontWeight: 600 }}>{expiry.level === 'expired' ? 'expired' : 'expiring soon'} · </span>}
                      {addedOf(d)}
                    </>
                  )} />
              );
            })}
          </DocCard>
        );
      })}
    </div>
  );
}
